import React, { useState, useEffect } from 'react';
import {
  Briefcase,
  Plus,
  Search,
  Edit,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  DollarSign,
  User,
  Loader2,
  PlayCircle
} from 'lucide-react';
import { jobAPI, customerAPI, userAPI, jobTimerAPI } from '../api/client';
import apiClient from '../api/client';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const STATUSES = [
  { id: 'all', label: 'All', color: 'bg-gray-500' },
  { id: 'pending', label: 'Pending', color: 'bg-yellow-500', badge: 'badge-pending' },
  { id: 'dispatched', label: 'Dispatched', color: 'bg-orange-500', badge: 'badge-pending' },
  { id: 'assigned', label: 'Assigned', color: 'bg-sky-500', badge: 'badge-active' },
  { id: 'scheduled', label: 'Scheduled', color: 'bg-blue-500' },
  { id: 'in_progress', label: 'In Progress', color: 'bg-purple-500', badge: 'badge-active' },
  { id: 'completed', label: 'Completed', color: 'bg-green-500', badge: 'badge-completed' },
  { id: 'invoiced', label: 'Invoiced', color: 'bg-teal-500', badge: 'badge-completed' },
  { id: 'paid', label: 'Paid', color: 'bg-emerald-500', badge: 'badge-completed' },
  { id: 'cancelled', label: 'Cancelled', color: 'bg-red-500', badge: 'badge-cancelled' },
];

const PRIORITIES = [
  { id: 'low', label: 'Low', color: 'text-gray-500' },
  { id: 'normal', label: 'Normal', color: 'text-blue-500' },
  { id: 'high', label: 'High', color: 'text-orange-500' },
  { id: 'urgent', label: 'Urgent', color: 'text-red-500' },
];

const SERVICE_TYPES = [
  'Key Duplication',
  'Lockout Service',
  'Key Programming',
  'Rekeying',
  'New Key Cutting',
  'Transponder Programming',
  'Remote Programming',
  'Ignition Repair',
  'Lock Installation',
  'Lock Repair',
  'Smart Lock Setup',
  'Other',
];

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [jobTypes, setJobTypes] = useState([]);
  const [pipelineLabels, setPipelineLabels] = useState({});

  const [formData, setFormData] = useState({
    customer_id: '',
    technician_id: '',
    title: '',
    description: '',
    service_type: '',
    priority: 'normal',
    status: 'pending',
    location: '',
    location_lat: null,
    location_lng: null,
    tech_split_percent: 50,
    labor_cost: 0,
    parts_cost: 0,
    notes: '',
  });

  useEffect(() => {
    loadData();
    // Fetch the job-type catalogue once
    apiClient.get('/api/job-types').then((r) => setJobTypes(r.data || [])).catch(() => {});
    // Fetch pipeline labels once
    apiClient.get('/api/job-pipeline').then((r) => setPipelineLabels(r.data?.labels || {})).catch(() => {});
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, searchQuery, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [jobsData, customersData, usersData] = await Promise.all([
        jobAPI.list(),
        customerAPI.list(),
        userAPI.list().catch(() => []), // Optional, requires admin role
      ]);
      setJobs(jobsData || []);
      setCustomers(customersData || []);
      setTechnicians(usersData || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = jobs;

    if (statusFilter !== 'all') {
      filtered = filtered.filter((job) => job.status === statusFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (job) =>
          job.title?.toLowerCase().includes(query) ||
          job.description?.toLowerCase().includes(query) ||
          job.service_type?.toLowerCase().includes(query)
      );
    }

    setFilteredJobs(filtered);
  };

  const handleOpenDialog = (job = null) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        customer_id: job.customer_id || '',
        technician_id: job.technician_id || '',
        title: job.title || '',
        description: job.description || '',
        service_type: job.service_type || '',
        priority: job.priority || 'normal',
        status: job.status || 'pending',
        location: job.location || '',
        labor_cost: job.labor_cost || 0,
        parts_cost: job.parts_cost || 0,
        notes: job.notes || '',
      });
    } else {
      setEditingJob(null);
      setFormData({
        customer_id: '',
        technician_id: '',
        title: '',
        description: '',
        service_type: '',
        priority: 'normal',
        status: 'pending',
        location: '',
        location_lat: null,
        location_lng: null,
        tech_split_percent: 50,
        labor_cost: 0,
        parts_cost: 0,
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.title) {
      toast.error('Customer and title are required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        total_cost: (formData.labor_cost || 0) + (formData.parts_cost || 0),
      };

      if (editingJob) {
        await jobAPI.update(editingJob.id, payload);
        toast.success('Job updated!');
      } else {
        await jobAPI.create(payload);
        toast.success('Job created!');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await jobAPI.delete(deleteConfirm.id);
      toast.success('Job deleted!');
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const handleStatusChange = async (job, newStatus) => {
    try {
      await jobAPI.update(job.id, { status: newStatus });
      toast.success(`Job status changed to ${newStatus.replace('_', ' ')}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAdvance = async (job) => {
    try {
      const result = await jobAPI.advance(job.id);
      const sx = result.side_effects || {};
      const extras = [];
      if (sx.invoice_number) extras.push(`Invoice ${sx.invoice_number} created`);
      if (sx.invoice_marked_paid) extras.push('Invoice marked paid');
      if (sx.archived_to_customer) extras.push('Archived to customer profile');
      toast.success(
        `${result.from_status.replace('_', ' ')} → ${result.to_status.replace('_', ' ')}` +
          (extras.length ? ` · ${extras.join(' · ')}` : ''),
      );
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not advance job');
    }
  };

  const getCustomerName = (customerId) => {
    const c = customers.find((c) => c.id === customerId);
    return c ? `${c.first_name} ${c.last_name}` : 'Unknown';
  };

  const getTechnicianName = (techId) => {
    const t = technicians.find((t) => t.id === techId);
    return t ? `${t.first_name} ${t.last_name}` : null;
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const getStatusBadgeClass = (status) => {
    const s = STATUSES.find((s) => s.id === status);
    return s?.badge || '';
  };

  // Stats
  const totalJobs = jobs.length;
  const pendingJobs = jobs.filter((j) => j.status === 'pending').length;
  const inProgressJobs = jobs.filter((j) => j.status === 'in_progress').length;

  // ---- Live timer tick — re-renders every 30s so any running timer's
  // ---- elapsed minutes update without a network call.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const startTimer = async (job) => {
    try {
      const r = await jobTimerAPI.start(job.id);
      toast.success(r.already_running ? 'Timer already running' : 'Timer started');
      loadData();
    } catch (e) { toast.error('Could not start timer'); }
  };

  const stopTimer = async (job) => {
    try {
      const r = await jobTimerAPI.stop(job.id);
      toast.success(`Stopped — added ${r.entry.minutes.toFixed(1)} min`);
      loadData();
    } catch (e) { toast.error('Could not stop timer'); }
  };

  const liveMinutes = (job) => {
    let total = Number(job.total_minutes || 0);
    if (job.time_started_at) {
      try {
        const startedAt = new Date(job.time_started_at);
        total += Math.max(0, (Date.now() - startedAt.getTime()) / 60000);
      } catch (_) { /* noop */ }
    }
    return total;
  };

  const fmtMinutes = (mins) => {
    const m = Math.floor(mins);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  };
  const completedJobs = jobs.filter((j) => j.status === 'completed').length;
  const totalRevenue = jobs
    .filter((j) => j.status === 'completed')
    .reduce((sum, j) => sum + (j.total_cost || 0), 0);

  return (
    <div className="space-y-6 fade-in" data-testid="jobs-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Jobs Management</h1>
          <p className="text-muted-foreground">Create, dispatch, and track all your jobs</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="btn-gradient-primary"
          data-testid="add-job-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Job
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="stat-blue border">
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Total Jobs</p>
            <p className="text-2xl font-bold">{totalJobs}</p>
          </CardContent>
        </Card>
        <Card className="stat-orange border">
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Pending</p>
            <p className="text-2xl font-bold text-orange-500">{pendingJobs}</p>
          </CardContent>
        </Card>
        <Card className="stat-purple border">
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">In Progress</p>
            <p className="text-2xl font-bold text-purple-500">{inProgressJobs}</p>
          </CardContent>
        </Card>
        <Card className="stat-green border">
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Completed</p>
            <p className="text-2xl font-bold text-green-500">{completedJobs}</p>
          </CardContent>
        </Card>
        <Card className="stat-cyan border">
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Revenue</p>
            <p className="text-xl font-bold text-cyan-500">{formatCurrency(totalRevenue)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search jobs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="job-search"
            />
          </div>
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList className="w-full justify-start overflow-x-auto no-scrollbar">
              {STATUSES.map((s) => (
                <TabsTrigger key={s.id} value={s.id}>
                  <span className={`w-2 h-2 rounded-full ${s.color} mr-2`}></span>
                  {s.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* Jobs List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="h-16 shimmer rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredJobs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Briefcase className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No jobs found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting filters'
                : 'Create your first job to get started'}
            </p>
            <Button onClick={() => handleOpenDialog()} className="btn-gradient-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create Job
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredJobs.map((job) => {
            const techName = getTechnicianName(job.technician_id);
            const priorityConfig = PRIORITIES.find((p) => p.id === job.priority);

            return (
              <Card
                key={job.id}
                className="card-hover"
                data-testid={`job-card-${job.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-semibold text-lg">{job.title}</h3>
                        <Badge className={getStatusBadgeClass(job.status)}>
                          {job.status?.replace('_', ' ')}
                        </Badge>
                        {priorityConfig && (
                          <Badge variant="outline" className={priorityConfig.color}>
                            {priorityConfig.label}
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">Customer</p>
                          <p className="font-medium flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {getCustomerName(job.customer_id)}
                          </p>
                        </div>
                        {job.service_type && (
                          <div>
                            <p className="text-xs text-muted-foreground">Service</p>
                            <p className="font-medium">{job.service_type}</p>
                          </div>
                        )}
                        {techName && (
                          <div>
                            <p className="text-xs text-muted-foreground">Technician</p>
                            <p className="font-medium">{techName}</p>
                          </div>
                        )}
                      </div>

                      {job.description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {job.description}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <p className="text-2xl font-bold text-green-500">
                        {formatCurrency(job.total_cost)}
                      </p>
                      <div className="flex gap-1 flex-wrap justify-end">
                        {/* Unified Progress button — uses pipelineLabels for the next-stage label */}
                        {pipelineLabels[job.status]?.next && (
                          <Button
                            size="sm"
                            onClick={() => handleAdvance(job)}
                            className="btn-gradient-success"
                            data-testid={`job-${job.id}-advance`}
                          >
                            <PlayCircle className="w-3 h-3 mr-1" />
                            {pipelineLabels[job.status].button} →
                          </Button>
                        )}
                        {/* Timer controls (always available except void/cancelled) */}
                        {job.status !== 'cancelled' && (
                          job.time_started_at ? (
                            <Button
                              size="sm"
                              onClick={() => stopTimer(job)}
                              className="bg-amber-500 hover:bg-amber-600 text-white"
                              data-testid={`job-${job.id}-stop-timer`}
                              title={`Running · ${fmtMinutes(liveMinutes(job))}`}
                            >
                              <Clock className="w-3 h-3 mr-1 animate-pulse" />
                              {fmtMinutes(liveMinutes(job))}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startTimer(job)}
                              data-testid={`job-${job.id}-start-timer`}
                              title="Start timer"
                            >
                              <Clock className="w-3 h-3 mr-1" />
                              {job.total_minutes ? fmtMinutes(job.total_minutes) : 'Timer'}
                            </Button>
                          )
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenDialog(job)}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteConfirm(job)}
                          className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingJob ? 'Edit Job' : 'Create New Job'}</DialogTitle>
            <DialogDescription>
              {editingJob ? 'Update job details' : 'Create a new job for a customer'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Job Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Honda Civic Key Replacement"
                  required
                  data-testid="job-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(val) => setFormData({ ...formData, customer_id: val })}
                >
                  <SelectTrigger data-testid="job-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name} - {c.phone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Technician</Label>
                <Select
                  value={formData.technician_id}
                  onValueChange={(val) => setFormData({ ...formData, technician_id: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Assign technician" />
                  </SelectTrigger>
                  <SelectContent>
                    {technicians.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.first_name} {t.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Service Type</Label>
                <Select
                  value={formData.service_type}
                  onValueChange={(val) => {
                    // Auto-populate labor_cost from the picked job-type's default
                    const jt = jobTypes.find((j) => j.id === val || j.label === val);
                    setFormData({
                      ...formData,
                      service_type: jt ? jt.label : val,
                      labor_cost: (formData.labor_cost ? formData.labor_cost : (jt?.default_labor ?? formData.labor_cost)),
                    });
                  }}
                >
                  <SelectTrigger data-testid="job-service-type-select">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobTypes.map((j) => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.label}
                        <span className="text-xs text-muted-foreground ml-2">
                          ~{j.default_minutes}min · ${j.default_labor}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(val) => setFormData({ ...formData, priority: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(val) => setFormData({ ...formData, status: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.filter((s) => s.id !== 'all').map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Location</Label>
                <AddressAutocomplete
                  value={formData.location}
                  onChange={(v) => setFormData({ ...formData, location: v })}
                  onSelect={(r) => setFormData({
                    ...formData,
                    location: r.address,
                    location_lat: r.lat,
                    location_lng: r.lng,
                  })}
                  placeholder="Service location — start typing..."
                  testid="job-location-autocomplete"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Tech Profit Split: <span className="text-primary font-bold">{formData.tech_split_percent || 50}%</span> to technician</Label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={formData.tech_split_percent ?? 50}
                  onChange={(e) => setFormData({ ...formData, tech_split_percent: parseInt(e.target.value, 10) })}
                  className="w-full"
                  data-testid="job-tech-split-slider"
                />
                <p className="text-xs text-muted-foreground">Used by the Reports page to calculate tech earnings vs company earnings.</p>
              </div>

              <div className="space-y-2">
                <Label>Labor Cost ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.labor_cost}
                  onChange={(e) => setFormData({ ...formData, labor_cost: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label>Parts Cost ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.parts_cost}
                  onChange={(e) => setFormData({ ...formData, parts_cost: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Job description, requirements..."
                  rows={3}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Internal notes..."
                  rows={2}
                />
              </div>

              {/* Total */}
              <div className="md:col-span-2 p-3 bg-muted rounded-lg flex items-center justify-between">
                <span className="font-medium">Total Cost:</span>
                <span className="text-2xl font-bold text-green-500">
                  {formatCurrency((formData.labor_cost || 0) + (formData.parts_cost || 0))}
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-gradient-primary" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : editingJob ? (
                  'Update Job'
                ) : (
                  'Create Job'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Job</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.title}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button onClick={handleDelete} className="btn-gradient-danger">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
